<template>
  <div>
    <v-card
        color="primary"
        class="card"
        min-width="200"
        @contextmenu="onRightClick"
        @dragover="showDropZone = true"
    >
      <!-- Overlay bei Import / Entfernt -->
      <div v-if="imported || task.removed" class="card-overlay">
        <v-icon size="130" color="white">mdi-check-circle</v-icon>
      </div>

      <!-- Overlay: Task noch nicht verarbeitet -->
      <div
          v-if="(loadingCustomization || !processed) && !unlockCard"
          class="card-overlay d-flex flex-column text-center"
      >
        <v-icon
            v-if="!loadingCustomization"
            @click="unlockCard = true"
            size="130"
            color="white"
        >
          mdi-lock
        </v-icon>
        <v-progress-circular v-else :value="true" :indeterminate="true" size="130px"></v-progress-circular>
        <h2 class="white--text">Waiting For Task Processing</h2>
      </div>

      <!-- Dropzone -->
      <div
          v-if="showDropZone"
          @click="showDropZone = false"
          class="d-flex"
      >
        <v-card
            class="card-overlay"
            @dragleave="showDropZone = false"
            @drop.prevent="dropFile"
            @dragenter.prevent
            @dragover.prevent
        >
          <v-icon
              :style="{ 'pointer-events': 'none' }"
              size="130"
              color="white"
          >
            mdi-select-drag
          </v-icon>
          {{ index }}
        </v-card>
      </div>

      <!-- Header -->
      <div class="card-header-function">
        <v-btn
            @click="() => downloadImage(displayImage, `${task.orderName}_${selectedVariantLabel}`, 'jpg')"
            color="primary"
            dark
            small
            fab
            class="card-header-button"
        >
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <TimeIndicator :creation-date="task.createdAt" />

        <v-btn
            @click="openTask"
            color="primary"
            dark
            rounded
            link
            class="card-header-button"
        >
          {{ task.orderName }}
          <v-icon x-small class="ml-3">mdi-open-in-new</v-icon>
        </v-btn>

        <OrderNoteTooltip
            :task="task"
            :on-save="saveOrderNote"
        />
        <div style="cursor: pointer" @click="() => (showPreviousTasks = true)">
          <OrderCountIndicator :order-count="task.orderCount" />
        </div>
        <SupportTooltip :order-id="task.orderId" />
      </div>

      <!-- Bildbereich -->
      <v-card-text class="image-container">
        <!-- Hauptbild: immer die aktuell gewählte Variante -->
        <v-img
            height="500px"
            position="center"
            :src="task.imgThumb"
            class="image"
            @click="openImageDialog"
        />
        <div class="task-product-name">
          {{ task.productType.name }}<br />
          {{ task?.customPrintVariantName || task.printVariantName }}
        </div>

        <!-- Original-Vorschau (falls vorhanden) -->
        <v-img
            v-if="originalImage"
            class="original-image"
            width="150px"
            :src="originalImage"
            @click="openImageDialog(UPSCALER_VARIANTS.ORIGINAL)"
        />
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="card-actions">
<!--        <v-btn-->
<!--            v-if="!resultsInTask && task?.productType?.isDigital === false"-->
<!--            dark-->
<!--            small-->
<!--            outlined-->
<!--            :loading="isUploading"-->
<!--            @click="(e) => pushToFulfill(e)"-->
<!--        >-->
<!--          Send To Fulfill-->
<!--        </v-btn>-->
        <v-chip
            v-if="selectedVariantLabel"
            small
            outlined
            class="ml-2"
            color="white"
            text-color="white"
            @click="resetSelectedVariant"
        >
          {{ selectedVariantLabel }}
          <v-icon size="small">mdi-close</v-icon>
        </v-chip>
        <v-btn dark color="success" small @click="openLiveEditor">
          <v-icon left>mdi-pencil</v-icon> Live Editor
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog für Bild + Upscaling -->
    <ImageDialog
        v-if="dialogImage"
        :show="!!dialogImage"
        :image="dialogImage"
        :onClose="closeImageDialog"
    >
      <div class="switch-upscale-container" v-if="dialogImage !== originalImage">
        <v-btn
            ripple
            class="mr-3"
            :dark="selectedVariant === UPSCALER_VARIANTS.ORIGINAL"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.ORIGINAL)"
        >
          Original
        </v-btn>

        <v-btn
            class="mr-3"
            :loading="upscaleStates[UPSCALER_VARIANTS.DEFAULT].loading"
            :dark="selectedVariant === UPSCALER_VARIANTS.DEFAULT"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.DEFAULT)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Default
        </v-btn>

        <v-btn
            class="mr-3"
            :loading="upscaleStates[UPSCALER_VARIANTS.PRO_CAR].loading"
            :dark="selectedVariant === UPSCALER_VARIANTS.PRO_CAR"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.PRO_CAR)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Pro (Car)
        </v-btn>

        <v-btn
            v-if="upscaleStates[UPSCALER_VARIANTS.PRO_PERSON]"
            :loading="upscaleStates[UPSCALER_VARIANTS.PRO_PERSON].loading"
            :dark="selectedVariant === UPSCALER_VARIANTS.PRO_PERSON"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.PRO_PERSON)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Pro (Person)
        </v-btn>
      </div>
    </ImageDialog>

    <!-- Kontextmenü -->
    <ListMenu
        :show="showMenu"
        :x="x"
        :y="y"
        :list-items="menuListItems"
    />

    <!-- Mail-Dialog -->
    <OrderMailDialog
        v-if="showMailDialog"
        :show="showMailDialog"
        :v2="true"
        :wait-for-sent-success="false"
        :on-close="() => (showMailDialog = false)"
        :order-id="task.orderId"
        mail-category="LIVE"
    />
    <LiveEditorDialog
        v-show="showLiveEditor"
        v-model="showLiveEditor"
        :task="task"
        :customizationData="task.customizationData"
        :orderName="task.orderName"
        :upscaledUrls="upscaleStates"
        @save="handleLiveEditorSave"
        :custom-artwork="customImageUrl"
        @approveProcessed="showLiveEditor = false"
        @approve="({ finalUrl, sendPreviewCustomer }) => pushToFulfill(null, finalUrl, sendPreviewCustomer)"
    />
    <PreviousTasksDialog
        v-if="showPreviousTasks"
        :show="!!showPreviousTasks"
        :on-close="() => (showPreviousTasks = false)"
        :task="task"
    />
  </div>
</template>

<script>
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import {mapActions, mapGetters} from "vuex";
import ImageDialog from "@/components/ImageDialog.vue";
import TimeIndicator from "@/components/TaskCards/components/TimeIndicator.vue";
import {downloadImageFromUrl, getTrackingUrl, openOrderModal} from "@/utils";
import OrderNoteTooltip from "@/components/OrderNoteTooltip.vue";
import OrderCountIndicator from "@/components/TaskCards/components/OrderCountIndicator.vue";
import SupportTooltip from "@/components/Support/SupportTooltip";
import ListMenu from "@/components/ListMenu.vue";
import OrderMailDialog from "@/components/OrderMailDialog.vue";
import { getFunctions, httpsCallable } from "firebase/functions";
import {EventBus, events} from "../../../event-bus";
import LiveEditorDialog from "@/components/FulfillOrders/LiveEditorDialog.vue";
import PreviousTasksDialog from "@/components/TaskCards/components/PreviousTasksDialog.vue";
import {permissions} from "@/constants";

const UPSCALER_VARIANTS = {
  ORIGINAL: "ORIGINAL",
  DEFAULT: "DEFAULT",
  PRO_CAR: "PRO_CAR",
  PRO_PERSON: "PRO_PERSON",
};

export default {
  name: "ImportTaskCard",
  components: {
    PreviousTasksDialog,
    LiveEditorDialog,
    OrderMailDialog,
    ListMenu,
    OrderCountIndicator,
    OrderNoteTooltip,
    TimeIndicator,
    ImageDialog,
    SupportTooltip,
  },
  props: {
    task: {
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    // Optional: falls du das von außen reinreichst
    resultsInTask: {
      type: Boolean,
      default: false,
    },
    selectedEditStyle: {
      default: "",
    },
  },
  data() {
    const customizationData = (this.task && this.task.customizationData) || {};
    const upscaledImage = customizationData.upscaledImage || null;

    return {
      showPreviousTasks: false,
      showLiveEditor: false,
      loadingCustomization: false,
      UPSCALER_VARIANTS,
      imported: false,
      unlockCard: false,
      showDropZone: false,
      customImage: null,
      customImageUrl: null,
      dialogImage: null,
      isUploading: false,
      showMenu: false,
      showMailDialog: false,
      x: 0,
      y: 0,

      upscaleStates: {
        [UPSCALER_VARIANTS.DEFAULT]: { loading: false, url: upscaledImage },
        [UPSCALER_VARIANTS.PRO_CAR]: { loading: false, url: null },
        [UPSCALER_VARIANTS.PRO_PERSON]: { loading: false, url: null },
      },

      selectedVariant: upscaledImage
          ? UPSCALER_VARIANTS.DEFAULT
          : UPSCALER_VARIANTS.ORIGINAL,

      menuListItems: [],
    };
  },
  computed: {
    ...mapGetters(['userPermissions', 'isAdmin']),
    processed() {
      return !this.task?.customizationData?.isProcessing;
    },
    artworkImage() {
      return this.task?.customizationData?.artworkImageUrl;
    },
    // Das Bild, das als Basis für Upscaling dient
    taskImage() {
      return this.customImageUrl || this.artworkImage || this.task.imgThumb;
    },
    customizationData() {
      return this.task.customizationData;
    },
    originalImage() {
      return this.task?.customizationData?.originalImage;
    },
    adminTasksAllowed() {
      return this.isAdmin || this.userPermissions.includes(permissions.MANAGE_TASKS)
    },
    // Bild, das gerade angezeigt werden soll (je nach Auswahl)
    displayImage() {
      if (this.customImageUrl) return this.customImageUrl
      switch (this.selectedVariant) {
        case UPSCALER_VARIANTS.DEFAULT:
        case UPSCALER_VARIANTS.PRO_CAR:
        case UPSCALER_VARIANTS.PRO_PERSON: {
          const state = this.upscaleStates[this.selectedVariant];
          return state && state.url ? state.url : this.taskImage;
        }
        case UPSCALER_VARIANTS.ORIGINAL:
        default:
          return this.taskImage;
      }
    },
    // Label für den Chip im Header
    selectedVariantLabel() {
      if (this.customImage) return "Custom Image"
      switch (this.selectedVariant) {
        case UPSCALER_VARIANTS.DEFAULT:
          return "Default";
        case UPSCALER_VARIANTS.PRO_CAR:
          return "Pro (Car)";
        case UPSCALER_VARIANTS.PRO_PERSON:
          return "Pro (Person)";
        case UPSCALER_VARIANTS.ORIGINAL:
        default:
          return null; // Keine Anzeige, wenn original
      }
    },
  },
  created() {
    // Menü hier aufsetzen, damit es this.* sauber nutzen kann
    this.menuListItems = [
      {
        icon: "mdi-download",
        onClick: () =>
            this.downloadImage(
                this.artworkImage || this.displayImage,
                `${this.task.orderName}_artwork`,
                "png"
            ),
        label: "Download Artwork",
      },
      {
        icon: "mdi-download",
        onClick: () =>
            this.downloadImage(
                this.originalImage || this.taskImage,
                `${this.task.orderName}_original`,
                "png"
            ),
        label: "Download Original",
      },
      {
        icon: "mdi-download",
        onClick: () =>
            this.resolveStorageUrl(
                this.displayImage,
                `${this.task.orderName}_original`,
                "png"
            ),
        label: "Download DEFAULT upscale",
      },
      {
        icon: "mdi-refresh",
        onClick: () =>
            this.getCustomizationData(),
        label: "Get Customization Data",
      },
      // {
      //   icon: "mdi-close-circle",
      //   onClick: () =>
      //       this.cancelTask({
      //         task: this.task,
      //       }),
      //   label: "Cancel Task",
      // },
      {
        icon: "mdi-email-outline",
        onClick: () => {
          this.showMailDialog = true;
        },
        label: "Send Mail",
        hide: !this.adminTasksAllowed
      },
      {
        icon: "mdi-email-search-outline",
        onClick: this.searchOrderInMails,
        label: "Search For Response",
        hide: !this.adminTasksAllowed
      },
      {
        icon: "mdi-clipboard-arrow-right",
        onClick: this.copyOrderTrackingUrl,
        label: "Copy Order Tracking URL",
      },
    ];
  },
  methods: {
    ...mapActions(["editTask", "cancelTask", "importLiveTaskFromShopify"]),

    openTask() {
      openOrderModal(this.task.orderId);
    },

    openLiveEditor() {
      this.showLiveEditor = true;
    },

    async handleLiveEditorSave(payload) {
      console.log("Saving Changes:", payload);
      // 1. Payload an deine Firebase Function oder API senden, um das Dokument zu updaten
      // 2. Neues Preview generieren lassen (falls gewünscht)

      // Beispiel:
      // await this.updateCustomization(payload.id, payload.configuration);

      // Snack bar
      EventBus.$emit(events.SNACKBAR, {
        message: "Changes saved locally (implement API call here)",
        type: "success",
      });
    },

    resetSelectedVariant() {
      this.customImage = null
      this.customImageUrl = null
      this.selectedVariant = UPSCALER_VARIANTS.ORIGINAL
    },

    saveOrderNote({ orderNote, orderNoteFiles }) {
      this.editTask({
        task: { ...this.task, orderNote, orderNoteFiles },
      });
    },

    onRightClick(e) {
      e.preventDefault();
      this.showMenu = false;
      this.x = e.clientX;
      this.y = e.clientY;
      this.$nextTick(() => {
        this.showMenu = true;
      });
    },
    pushToFulfill(e, url = null, sendPreviewCustomer = false) {
      if (e && !e.ctrlKey && !e.metaKey) {
        EventBus.$emit(events.SNACKBAR, {message: "Halte Strg/Cmd gedrückt, um das Bild zu pushen." ,type: "info"})
        return;
      }
      this.isUploading = true;
      this.showLiveEditor = false;
      this.importLiveTaskFromShopify({
        task: {
          ...this.task,
        },
        customImage: url || this.customImage,
        callBack: () => {
          this.isUploading = false;
          this.imported = true;
        },
        sendPreviewCustomer: sendPreviewCustomer,
      });
    },

    dropFile(e) {
      e.preventDefault();
      this.showDropZone = false;

      const droppedFiles = e.dataTransfer ? e.dataTransfer.files : e.target.files;
      if (!droppedFiles || !droppedFiles.length) return;

      this.customImage = droppedFiles[0];

      const reader = new FileReader();
      reader.onload = (event) => {
        this.customImageUrl = event.target.result;
        // Ab hier könntest du den Upload zum Server triggern, falls nötig
      };
      reader.readAsDataURL(droppedFiles[0]);
    },

    // Dialog-Handling
    openImageDialog(variant) {
      if (variant === UPSCALER_VARIANTS.ORIGINAL) {
        this.dialogImage = this.originalImage;
      } else {
        this.dialogImage = this.displayImage;
      }
    },

    closeImageDialog() {
      this.dialogImage = null;
    },

    // Auswahl der Variante ohne Upscale-Logik
    selectVariant(variant) {
      this.selectedVariant = variant;
      // wenn Dialog offen ist, Bild direkt aktualisieren
      if (this.dialogImage) {
        this.dialogImage = this.displayImage;
      }
    },

    /**
     * Wird von den Buttons aufgerufen:
     * - ORIGINAL: nur Auswahl
     * - andere: wenn URL vorhanden → Auswahl; sonst Upscale starten
     */
    handleUpscaleClick(variant) {
      if (variant === UPSCALER_VARIANTS.ORIGINAL) {
        this.selectVariant(variant);
        return;
      }

      const state = this.upscaleStates[variant];
      if (!state) return;

      if (state.loading) return;

      // Wenn schon fertig geupscaled: nur auswählen
      if (state.url) {
        this.selectVariant(variant);
        return;
      }

      // Sonst Upscale starten
      this.startUpscale(variant);
    },

    async getCustomizationData() {
      this.loadingCustomization = true;
      try {
        const functions = getFunctions();
        const getCustomizationData = httpsCallable(functions, 'getCustomizationData');
        const result = await getCustomizationData({
          id: this.task.customizationData.id
        });
      } catch (error) {
        console.error("Upscale failed:", error);
        EventBus.$emit(events.SNACKBAR, {
          message: "Fehler beim Customization data!",
          type: "error",
        });
      } finally {
        this.loadingCustomization = false;
      }
    },

    /**
     * Startet den Upscale-Prozess für das aktuelle taskImage.
     * Hier später deine echte API / Function einhängen.
     */
    async startUpscale(variant) {
      const state = this.upscaleStates[variant];
      if (!state) return;

      this.$set(this.upscaleStates[variant], "loading", true);

      try {
        const imageUrl = this.taskImage;
        console.log(imageUrl)
        console.log(this.originalImage)
        if (!imageUrl) {
          throw new Error("No task image available for upscaling");
        }

        // TODO: hier echte Upscale-Integration einbauen
        // z. B. const url = await this.$api.upscale({ variant, imageUrl })
        try {
          const functions = getFunctions();
          const upscaleGemini = httpsCallable(functions, 'upscaleGemini');
          const result = await upscaleGemini({
            artworkUrl: imageUrl,
            originalUrl: this.originalImage,
            promptId: variant,
            resolution: "4K"
          });

          if (result?.data?.ok) {
            this.$set(this.upscaleStates[variant], "url", result.data.url);
            this.selectVariant(variant);
            console.log("Upscaled Image URL:", result.data.url);
          } else {
            throw new Error("Failed to upscale image");
          }
        } catch (error) {
          console.error("Upscale failed:", error);
          EventBus.$emit(events.SNACKBAR, {
            message: "Fehler beim Upscalen!",
            type: "error",
          });
        }
      } catch (error) {
        // hier könntest du z.B. $error(...) verwenden
        // this.$error("Upscaling failed");
        EventBus.$emit(events.SNACKBAR, {
          message: "Fehler beim Upscalen!",
          type: "error",
        });
        console.error("Upscale error", error);
      } finally {
        this.$set(this.upscaleStates[variant], "loading", false);
      }
    },

    /**
     * Platzhalter für dein echtes Upscale-API-Call.
     * Gibt aktuell nur eine Mock-URL zurück.
     */
    mockUpscaleRequest(variant, imageUrl) {
      // Du kannst hier z.B. den Variantennamen anhängen,
      // um im Debug zu sehen, was passiert.
      return Promise.resolve(
          `https://picsum.photos/seed/${variant}/800/400`
      );
    },

    // Platzhalter, falls du sie schon nutzt (im Menü referenziert)
    searchOrderInMails() {
      window.open(
          `https://mail.google.com/mail/u/?authuser=lisa@liebespferd.com&tab=wm#search/%23${
              this.task.orderName.split("#")[1]
          }`,
          "_blank"
      );
    },
    copyOrderTrackingUrl() {
      navigator.clipboard.writeText(getTrackingUrl(this.task.countryCode, this.task.orderKey))
    },
    async downloadImage(url, filename, fileType = "png") {
      if (!url) return;
      const furl = await this.resolveStorageUrl(url)
      const filename_with_edit_style = `${filename}_E${this.selectedEditStyle.value}`
      await downloadImageFromUrl(furl, filename_with_edit_style, () => {}, fileType);
    },
    async resolveStorageUrl(input) {
      const storage = getStorage();

      let path = input;

      // 1. Prüfen ob es eine URL ist
      if (input.startsWith("http")) {
        console.log(input)

        try {
          const url = new URL(input);

          // Format: https://storage.googleapis.com/<bucket>/<path>
          const parts = url.pathname.split("/");

          // Entferne den Bucket-Namen (index 1)
          parts.shift(); // erste Leerstelle
          parts.shift(); // bucket

          path = parts.join("/");
          console.log(path);
        } catch (e) {
          console.warn("Ungültige URL:", input);
        }
      }

      // 2. Firebase Storage Pfad auf gültige URL konvertieren
      const fileRef = ref(storage, path);
      return await getDownloadURL(fileRef);
    }
  },
};
</script>

<style lang="scss" scoped>
.card-actions {
  display: flex;
  justify-content: space-between;
}
.selected-variant {
  color: white;
  padding: 4px 8px;
  background: #8a8a8a;
  border-radius: 5px;
}
.card {
  position: relative;
  overflow: hidden;

  .v-card__text {
    padding: 0;
  }
}

.original-image {
  position: absolute;
  bottom: 0;
  right: 0;
  max-height: 30%;
  z-index: 2;
  cursor: pointer;
  border-top-left-radius: 10px;

  &:hover {
    transform: scale(1.1);
    transform-origin: bottom right;
  }
}

.card-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 3;
  background: rgba(115, 115, 115, 0.7);
}

.card-header-function {
  z-index: 3;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  .v-btn {
    margin: 12px;
  }
}

.image-container {
  position: relative;
}

.image {
  cursor: pointer;
  z-index: 1;
}

.watermark-preview {
  font-size: 60px;
  line-height: 60px;
  font-weight: bold;
  position: absolute;
  z-index: 2;
  transform: rotate(-45deg);
  bottom: 50%;
  pointer-events: none;
  color: rgba(100, 99, 99, 0.88);
  display: flex;
  align-items: center;
  background: white;
  width: fit-content;
  border-radius: 5px;
  padding: 0 5px;
}

.task-referrer {
  padding: 6px;
  border-radius: 5px;
  position: absolute;
  color: white;
  font-weight: bold;
  max-width: 50%;
  bottom: 78px;
  margin: 10px;
  z-index: 2;
}

.task-product-name {
  padding: 6px;
  border-radius: 5px;
  position: absolute;
  background: rgba(0, 0, 0, 0.87);
  max-width: 50%;
  color: white;
  bottom: 10px;
  margin: 10px;
  z-index: 2;
}

.task-product-extras{
  padding: 6px;
  border-radius: 5px;
  position: absolute;
  background: rgba(0, 0, 0, 0.87);
  max-width: 50%;
  color: white;
  bottom: 70px;
  margin: 10px;
  z-index: 2;
}

.loading-template {
  position: absolute;
  z-index: 2;
}

.task-template {
  cursor: pointer;
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 2;
  transform-origin: bottom right;
  transform: scale(1);
  transition: all 0.5s;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 140px;
  min-height: 80px;

  &:hover {
    transform: scale(2);
  }
}

.teeinblue-controls {
  position: absolute;
  width: 100%;
  height: 30px;
  z-index: 10;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 5px;

  .action-icon {
    font-size: 15px;
    margin-left: 1px;
    background-color: white;
    border-radius: 50%;
    padding: 5px;
  }
}

.task-template-thumbnail {
  z-index: 3;
}

.switch-upscale-container {
  z-index: 4;
  position: relative;
  bottom: 5rem;
  width: 100%;
  display: flex;
  justify-content: center;
}

.use-upscale-indicator {
  display: flex;
  color: white;
  width: 100%;
  justify-content: center;
  font-weight: bold;
}

.import-button-container {
  display: flex;
  z-index: 3;
}
</style>
